import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { isSameDay } from 'date-fns';
import {
  ChangeEvent,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ErrorScreen } from '../core/ErrorScreen';
import { LoadingSpinner } from '../core/LoadingSpinner';
import {
  useGetFsDirectories,
  useGetFsDirectoryProtocols,
  useGetFsDirectoryTypes,
  useScanFsDirectories,
} from '../hooks/reactQuerys/FsDirectoryQueries';
import { useGetLegalRequirementStates } from '../hooks/reactQuerys/LegalRequirementQueries';
import {
  useGetLegalTransactionRecordNumbers,
  useGetLegalTransactions,
  useGetLegalTransactionTransactionNumbers,
} from '../hooks/reactQuerys/LegalTransactionQueries';
import { useLocalStorage } from '../hooks/useLocalStorage';
import {
  FS_TIME_FILTER_OPTIONS,
  getInitialFilterObject,
  IFsDirectoryFilterSettings,
  IFSScanResult,
  resolveTimeFilter,
  type IFSDirectory,
} from '../Interfaces/IFSDirectory';
import type { IFSDirectoryType } from '../Interfaces/IFSDirectoryType';
import { DocumentPortalAccordion } from './DocumentPortalAccordion/DocumentPortalAccordion';

const getDocumentsForDate = (
  fsScanResults: IFSScanResult[],
  currentDate: Date
) =>
  fsScanResults.filter(result =>
    isSameDay(new Date(result.created_at), currentDate)
  );

export const DocumentPortalOverviewLayout = ({
  children,
  filterObject,
  setFilterObject,
  searchTerm,
  setSearchTerm,
  refetchFsScanResults,
  isFetchingFsScanResults,
  fsDirectories,
  allFsDirectoryTypes,
}: {
  children: ReactNode;
  filterObject: IFsDirectoryFilterSettings;
  setFilterObject: (filterObject: IFsDirectoryFilterSettings) => void;
  searchTerm: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  refetchFsScanResults: () => void;
  isFetchingFsScanResults: boolean;
  fsDirectories: IFSDirectory[];
  allFsDirectoryTypes: IFSDirectoryType[];
}) => {
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Box>
          <Typography variant='h4'>Posteingang</Typography>
          <Typography variant='body2'>
            (Rechter Mausklick wird unterstützt)
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                value={Boolean(filterObject.show_empty_directories)}
                onChange={() =>
                  setFilterObject({
                    ...filterObject,
                    show_empty_directories:
                      !filterObject.show_empty_directories,
                  })
                }
                defaultChecked={Boolean(filterObject.show_empty_directories)}
              />
            }
            label='Leere Tage'
          />
          <FormControlLabel
            control={
              <Switch
                value={Boolean(filterObject.show_sorted_directories)}
                onChange={() =>
                  setFilterObject({
                    ...filterObject,
                    show_sorted_directories:
                      !filterObject.show_sorted_directories,
                  })
                }
                defaultChecked={Boolean(filterObject.show_sorted_directories)}
              />
            }
            label='Voll sortierte Tage'
          />

          <Button
            onClick={() => refetchFsScanResults()}
            variant='outlined'
            disabled={isFetchingFsScanResults}
          >
            {isFetchingFsScanResults ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant='body2'>Wird aktualisiert</Typography>
                <CircularProgress size={20} />
              </Box>
            ) : (
              'Aktualisieren'
            )}
          </Button>
        </Box>
      </Box>
      <Grid container spacing={2} mt={2}>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel id='folder-type-label' shrink>
              Ordnerart
            </InputLabel>
            <Select
              labelId='folder-type-label'
              id='folder-type'
              type='select'
              size='small'
              notched
              label='Ordnerart'
              value={filterObject.idFSDirectoryType}
              disabled={!fsDirectories || fsDirectories.length === 0}
              onChange={e => {
                const newFolderTypeValue = Number(e.target.value);

                setFilterObject({
                  ...filterObject,
                  idFSDirectoryType: newFolderTypeValue,
                  idFSDirectoryArray: fsDirectories
                    .filter(fsDir =>
                      newFolderTypeValue === 0
                        ? true
                        : newFolderTypeValue === fsDir.idFSDirectoryType
                    )
                    .map(fsDir => fsDir.idFSDirectory),
                });
              }}
            >
              {allFsDirectoryTypes.map(directoryType => (
                <MenuItem
                  key={`search-fstye-${directoryType.idFSDirectoryType}`}
                  value={directoryType.idFSDirectoryType}
                >
                  {directoryType.FSDirectoryType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel id='folder-label' shrink>
              Ordner
            </InputLabel>
            <Select
              labelId='folder-label'
              placeholder='Ordner wählen'
              id='folder'
              multiple
              notched
              label='Ordner'
              value={filterObject.idFSDirectoryArray}
              disabled={!fsDirectories || fsDirectories.length === 0}
              onChange={e => {
                const value = e.target.value;

                if (value && typeof value !== 'string') {
                  setFilterObject({
                    ...filterObject,
                    idFSDirectoryArray: value,
                  });
                }
              }}
              renderValue={selectedValues =>
                selectedValues
                  .map(
                    selectedValue =>
                      fsDirectories
                        .filter(fsDir =>
                          filterObject.idFSDirectoryType === 0
                            ? true
                            : filterObject.idFSDirectoryType ===
                              fsDir.idFSDirectoryType
                        )
                        .find(fsDir => fsDir.idFSDirectory === selectedValue)
                        ?.Name
                  )
                  .join(', ')
              }
              size='small'
              fullWidth
            >
              {fsDirectories
                .filter(fsDir =>
                  filterObject.idFSDirectoryType === 0
                    ? true
                    : filterObject.idFSDirectoryType === fsDir.idFSDirectoryType
                )
                .map(fsDir => (
                  <MenuItem
                    key={`fs-dir-${fsDir.idFSDirectory}`}
                    value={fsDir.idFSDirectory}
                  >
                    <Checkbox
                      checked={filterObject.idFSDirectoryArray.includes(
                        fsDir.idFSDirectory
                      )}
                    />
                    <ListItemText primary={fsDir.Name} />
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel id='time-filter-label' shrink>
              Zeitraum
            </InputLabel>
            <Select
              labelId='time-filter-label'
              id='time-filter'
              size='small'
              notched
              label='Zeitraum'
              value={resolveTimeFilter(filterObject.create_time_filter)}
              onChange={e =>
                setFilterObject({
                  ...filterObject,
                  create_time_filter: resolveTimeFilter(e.target.value),
                })
              }
            >
              {FS_TIME_FILTER_OPTIONS.map(option => (
                <MenuItem
                  key={`fs-time-filter-${option.value}`}
                  value={option.value}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <TextField
            label='Suche'
            value={searchTerm}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSearchTerm(event.target.value)
            }
            size='small'
            fullWidth
          />
        </Grid>
      </Grid>
      <Box sx={{ mt: 4 }}>{children}</Box>
    </>
  );
};

export const DocumentPortalOverview = () => {
  const { fsDirectoryTypes, isLoadingFsDirectoryTypes } =
    useGetFsDirectoryTypes();

  const { fsDirectoryProtocols, isLoadingFsDirectoryProtocols } =
    useGetFsDirectoryProtocols();

  const { fsDirectories, isLoadingFsDirectories } = useGetFsDirectories();

  const { legalTransactions, isLoadingLegalTransactions } =
    useGetLegalTransactions();

  const {
    legalTransactionRecordNumbers,
    isLoadingLegalTransactionRecordNumbers,
  } = useGetLegalTransactionRecordNumbers();

  const {
    legalTransactionTransactionNumbers,
    isLoadingLegalTransactionTransactionNumbers,
  } = useGetLegalTransactionTransactionNumbers();

  const { legalRequirementStates, isLoadingLegalRequirementStates } =
    useGetLegalRequirementStates();

  const { value: filterObject, setValue: setFilterObject } =
    useLocalStorage<IFsDirectoryFilterSettings>(
      'fs_directory_filter_object',
      getInitialFilterObject()
    );

  useEffect(() => {
    if (!fsDirectories) return;

    const dirIds = fsDirectories.map(fsDir => fsDir.idFSDirectory);
    const ids = filterObject.idFSDirectoryArray;

    const invalidIds = ids.filter(id => !dirIds.includes(id));

    if (invalidIds.length > 0) {
      setFilterObject({
        ...filterObject,
        idFSDirectoryArray: ids.filter(id => dirIds.includes(id)),
      });
    }
  }, [fsDirectories]);

  const completeFilterObject = useMemo(() => {
    // Ein in localStorage liegender Filter kann aelter sein als das Feld:
    // `useLocalStorage` merged keine Defaults, und ohne gueltigen Zeitraum
    // antwortet /fsdirectory/scan mit 400.
    const create_time_filter = resolveTimeFilter(
      filterObject.create_time_filter
    );

    if (!fsDirectories) {
      return { ...filterObject, create_time_filter };
    }

    return {
      ...filterObject,
      create_time_filter,
      idFSDirectoryArray: filterObject.idFSDirectoryArray.map(fsDir =>
        Number(fsDir)
      ),
    };
  }, [filterObject, fsDirectories]);

  const {
    fsScanResults,
    isLoadingFsScanResults,
    refetchFsScanResults,
    isFetchingFsScanResults,
  } = useScanFsDirectories(completeFilterObject);

  const [searchTerm, setSearchTerm] = useState('');

  const helperDates = useMemo(() => {
    // Create an array of dates for the last 31 days (today + 30 previous days)
    return Array.from({ length: 31 }, (_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - index);
      return new Date(date); // Create a new Date object to avoid reference issues
    });
  }, []); // Empty dependency array means this will only be calculated once

  const allFsDirectoryTypes = useMemo(() => {
    if (!fsDirectoryTypes) {
      return [
        {
          idFSDirectoryType: 0,
          FSDirectoryType: 'Alle',
        },
      ];
    }

    return [
      {
        idFSDirectoryType: 0,
        FSDirectoryType: 'Alle',
      },
      ...fsDirectoryTypes,
    ];
  }, [fsDirectoryTypes]);

  if (
    isLoadingFsDirectories ||
    isLoadingFsDirectoryTypes ||
    isLoadingFsDirectoryProtocols ||
    isLoadingLegalTransactions ||
    isLoadingLegalTransactionRecordNumbers ||
    isLoadingLegalTransactionTransactionNumbers ||
    isLoadingLegalRequirementStates ||
    isLoadingFsScanResults
  ) {
    return (
      <DocumentPortalOverviewLayout
        filterObject={filterObject}
        setFilterObject={setFilterObject}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        refetchFsScanResults={refetchFsScanResults}
        isFetchingFsScanResults={isFetchingFsScanResults}
        fsDirectories={fsDirectories || []}
        allFsDirectoryTypes={allFsDirectoryTypes}
      >
        <LoadingSpinner height='60vh' />
      </DocumentPortalOverviewLayout>
    );
  }

  if (
    !fsDirectories ||
    !fsDirectoryTypes ||
    !fsDirectoryProtocols ||
    !legalTransactions ||
    !legalRequirementStates ||
    !legalTransactionRecordNumbers ||
    !legalTransactionTransactionNumbers ||
    !fsScanResults
  ) {
    return (
      <DocumentPortalOverviewLayout
        filterObject={filterObject}
        setFilterObject={setFilterObject}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        refetchFsScanResults={refetchFsScanResults}
        isFetchingFsScanResults={isFetchingFsScanResults}
        fsDirectories={fsDirectories || []}
        allFsDirectoryTypes={allFsDirectoryTypes}
      >
        <ErrorScreen height='60vh' />
      </DocumentPortalOverviewLayout>
    );
  }

  return (
    <DocumentPortalOverviewLayout
      filterObject={filterObject}
      setFilterObject={setFilterObject}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      refetchFsScanResults={refetchFsScanResults}
      isFetchingFsScanResults={isFetchingFsScanResults}
      fsDirectories={fsDirectories}
      allFsDirectoryTypes={allFsDirectoryTypes}
    >
      {fsDirectories.length === 0 && (
        <Grid item xs={12}>
          <Alert severity='warning'>
            <b>Es wurden kein eingebundener Ordner gefunden!</b>
            <br />
            Ein Administrator muss in der Ordnerverwaltung einen Ordner anlegen,
            dass dieser im Posteingang durchsuchbar ist.
          </Alert>
        </Grid>
      )}
      <Box sx={{ mt: 4 }}>
        {searchTerm === '' ? (
          <>
            {helperDates
              .filter(currentDate => {
                const docsForDate = getDocumentsForDate(
                  fsScanResults,
                  currentDate
                );

                if (
                  !filterObject.show_empty_directories &&
                  docsForDate.length === 0
                ) {
                  return false;
                }

                if (!filterObject.show_sorted_directories) {
                  return (
                    docsForDate.length === 0 ||
                    docsForDate.some(doc => !doc.isSorted)
                  );
                }

                return true;
              })
              .map(currentDate => (
                <DocumentPortalAccordion
                  key={`document-accordinion-${currentDate.toISOString()}`}
                  currentDate={currentDate}
                  fsScanResults={getDocumentsForDate(
                    fsScanResults,
                    currentDate
                  )}
                  legalTransactions={legalTransactions}
                  legalReqStates={legalRequirementStates}
                  recordNumbers={legalTransactionRecordNumbers}
                  transactionNumbers={legalTransactionTransactionNumbers}
                />
              ))}
          </>
        ) : (
          <>
            {Array.from(
              new Set(
                fsScanResults
                  .filter(result =>
                    result.fileName
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase())
                  )
                  .map(result => result.created_at.split('T')[0])
              )
            ).map(dateStr => {
              const currentDate = new Date(dateStr);
              return (
                <DocumentPortalAccordion
                  key={`document-accordinion-${dateStr}`}
                  currentDate={currentDate}
                  fsScanResults={fsScanResults.filter(
                    result =>
                      result.fileName
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) &&
                      isSameDay(new Date(result.created_at), currentDate)
                  )}
                  legalTransactions={legalTransactions}
                  legalReqStates={legalRequirementStates}
                  recordNumbers={legalTransactionRecordNumbers}
                  transactionNumbers={legalTransactionTransactionNumbers}
                  openAsOpen
                />
              );
            })}
          </>
        )}
      </Box>
    </DocumentPortalOverviewLayout>
  );
};
